import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/shared/components/ui/select";
import TrailTimeline from '../../components/trails/TrailTimeline';
import TrailEventModal from '../../components/trails/TrailEventModal';
import {
  Activity,
  Search,
  Download,
  Calendar,
  RefreshCw,
  Truck,
  Package,
  MapPin } from
'lucide-react';

const OperationalTrail = () => {const { t } = useTranslation("wms");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewType, setViewType] = useState('vertical');
  const [filters, setFilters] = useState({
    entityType: 'all',
    entityId: '',
    dateRange: '24h'
  });

  useEffect(() => {
    fetchEvents();
  }, [filters.dateRange]);

  const fetchEvents = async () => {
    setLoading(true);
    // Use mock data for demo
    setEvents(getMockOperationalEvents());
    setLoading(false);
  };

  const getMockOperationalEvents = () => [
  {
    id: 'OPS-001',
    event_type: 'OPS.ORDER.STATUS_CHANGED',
    trail_type: 'OPERATIONAL',
    timestamp_server: new Date(Date.now() - 30000).toISOString(),
    entity_type: 'ORDER',
    entity_id: 'ORD-2026-001',
    payload: {
      previous_status: 'PACKED',
      new_status: 'SHIPPED',
      triggered_by: 'SCAN_GATE'
    }
  },
  {
    id: 'OPS-002',
    event_type: 'OPS.ROUTE.STARTED',
    trail_type: 'OPERATIONAL',
    timestamp_server: new Date(Date.now() - 120000).toISOString(),
    entity_type: 'ROUTE',
    entity_id: 'RT-2026-001',
    payload: {
      previous_status: 'LOADED',
      new_status: 'IN_PROGRESS',
      triggered_by: 'DRIVER_ACTION',
      metadata: { driver: 'John Smith', stops: 8 }
    }
  },
  {
    id: 'OPS-003',
    event_type: 'OPS.STOP.ARRIVED',
    trail_type: 'OPERATIONAL',
    timestamp_server: new Date(Date.now() - 300000).toISOString(),
    entity_type: 'STOP',
    entity_id: 'STOP-001',
    payload: {
      previous_status: 'EN_ROUTE',
      new_status: 'ARRIVED',
      triggered_by: 'GEOFENCE',
      metadata: { customer: 'ABC Corp' }
    }
  },
  {
    id: 'OPS-004',
    event_type: 'OPS.STOP.DELIVERED',
    trail_type: 'OPERATIONAL',
    timestamp_server: new Date(Date.now() - 600000).toISOString(),
    entity_type: 'STOP',
    entity_id: 'STOP-001',
    payload: {
      previous_status: 'ARRIVED',
      new_status: 'DELIVERED',
      triggered_by: 'POD_CAPTURED',
      metadata: { packages: 3, pod_id: 'POD-001' }
    }
  },
  {
    id: 'OPS-005',
    event_type: 'OPS.PACKAGE.OUT_FOR_DELIVERY',
    trail_type: 'OPERATIONAL',
    timestamp_server: new Date(Date.now() - 1800000).toISOString(),
    entity_type: 'PACKAGE',
    entity_id: 'PKG-001',
    payload: {
      previous_status: 'AT_HUB',
      new_status: 'OUT_FOR_DELIVERY',
      triggered_by: 'ROUTE_STARTED'
    }
  },
  {
    id: 'OPS-006',
    event_type: 'OPS.SHIPMENT.COMPLETED',
    trail_type: 'OPERATIONAL',
    timestamp_server: new Date(Date.now() - 3600000).toISOString(),
    entity_type: 'SHIPMENT',
    entity_id: 'SHP-001',
    payload: {
      previous_status: 'IN_TRANSIT',
      new_status: 'DELIVERED',
      triggered_by: 'ALL_PACKAGES_DELIVERED'
    }
  }];


  const handleSearch = () => {
    const filtered = getMockOperationalEvents().filter((event) => {
      if (filters.entityType !== 'all' && event.entity_type !== filters.entityType) return false;
      if (filters.entityId && !event.entity_id.includes(filters.entityId)) return false;
      return true;
    });
    setEvents(filtered);
  };

  return (
    <div className="p-6 space-y-6" data-testid="operational-trail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
            <Activity className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{t("operationalTrail", "Operational Trail")}</h1>
            <p className="text-muted-foreground">{t("statusTimelineUberstyle", "Status timeline - Uber-style tracking")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border p-1">
            <Button
              variant={viewType === 'vertical' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewType('vertical')}>{t("vertical", "Vertical")}


            </Button>
            <Button
              variant={viewType === 'horizontal' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewType('horizontal')}>{t("horizontal", "Horizontal")}


            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={fetchEvents}>
            <RefreshCw className="h-4 w-4 mr-2" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />{t("export", "Export")}

          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-end gap-4">
            <div className="space-y-2 flex-1">
              <label className="text-sm font-medium">{t("entityType", "Entity Type")}</label>
              <Select
                value={filters.entityType}
                onValueChange={(v) => setFilters((prev) => ({ ...prev, entityType: v }))}>
                
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allTypes", "All Types")}</SelectItem>
                  <SelectItem value="ORDER">{t("order", "Order")}</SelectItem>
                  <SelectItem value="SHIPMENT">{t("shipment", "Shipment")}</SelectItem>
                  <SelectItem value="ROUTE">{t("route", "Route")}</SelectItem>
                  <SelectItem value="STOP">{t("stop", "Stop")}</SelectItem>
                  <SelectItem value="PACKAGE">{t("package", "Package")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 flex-1">
              <label className="text-sm font-medium">{t("entityId", "Entity ID")}</label>
              <Input
                placeholder="ORD-001, RT-001, PKG-001..."
                value={filters.entityId}
                onChange={(e) => setFilters((prev) => ({ ...prev, entityId: e.target.value }))} />
              
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("period", "Period")}</label>
              <Select
                value={filters.dateRange}
                onValueChange={(v) => setFilters((prev) => ({ ...prev, dateRange: v }))}>
                
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">{t("last24h", "Last 24h")}</SelectItem>
                  <SelectItem value="7d">{t("last7Days", "Last 7 days")}</SelectItem>
                  <SelectItem value="30d">{t("last30Days", "Last 30 days")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSearch}>
              <Search className="h-4 w-4 mr-2" />{t("search", "Search")}

            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t("showing", "Showing")}
          <strong>{events.length}</strong>{t("statusChanges", "status changes")}
        </p>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1">
            <Truck className="h-3 w-3" />{t("routes", "Routes")}

          </Badge>
          <Badge variant="outline" className="gap-1">
            <MapPin className="h-3 w-3" />{t("stops", "Stops")}

          </Badge>
          <Badge variant="outline" className="gap-1">
            <Package className="h-3 w-3" />{t("packages", "Packages")}

          </Badge>
        </div>
      </div>

      {/* Timeline */}
      <Card>
        <CardContent className="p-6">
          {loading ?
          <div className="text-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
              <p className="mt-2 text-muted-foreground">{t("loadingEvents", "Loading events...")}</p>
            </div> :

          <TrailTimeline
            events={events}
            onEventClick={setSelectedEvent}
            defaultView={viewType}
            showViewToggle={false} />

          }
        </CardContent>
      </Card>

      {/* Event Detail Modal */}
      {selectedEvent &&
      <TrailEventModal
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)} />

      }
    </div>);

};

export default OperationalTrail;